import React from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { List } from 'immutable';

import useAppContext from 'flow/hooks/useAppContext';
import MenuItem from 'flow/components/ui-components/MenuItem';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import { TypedMap } from 'flow/types/TypedMap';
import CallButtonMenuItem from './CallButtonMenuItem';
import {
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getTechnicalNumberFromContact,
} from 'flow/utils/extension-utils';
import { PbxSharedVoiceMessage } from 'flow/types/VoiceMessage';
import { removeSharedVoiceMessage } from 'flow/actions/pbx-actions';
import {
    createVoiceMessageAction,
    deleteVoiceMessageAction,
} from 'flow/actions/voicemail-actions';
import { extractName, sharedVoiceMessageUrlPath } from 'flow/utils';
import { VoiceMessageActions } from 'flow/types/Voicemail';
import { getCallActionMarkedAsHandled } from 'flow/utils/call-records-utils';
import ContextMenu from '../ContextMenu';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    voiceMessage: TypedMap<PbxSharedVoiceMessage>;
    entityKey: string;
    voiceMessagesAction: List<TypedMap<VoiceMessageActions>>;
    setContextMenuIsActive: (value: boolean) => void;
}
const PbxVoiceMessageContextMenu = ({
    entityKey,
    voiceMessage,
    voiceMessagesAction,
    setContextMenuIsActive,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    return (
        <ContextMenu
            triggerLabel={getMoreOptionsLabel(
                i18next,
                extractName(voiceMessage.get('contact')),
            )}
            className="flex size-8 items-center justify-center bg-gray-200 p-2 hover:bg-gray-300"
            setActive={setContextMenuIsActive}
        >
            <CallButtonMenuItem
                fixedNumber={getFixedNumberFromContact(
                    voiceMessage.get('contact'),
                )}
                technicalNumber={getTechnicalNumberFromContact(
                    voiceMessage.get('contact'),
                )}
                mobileNumber={getMobileNumberFromContact(
                    voiceMessage.get('contact'),
                )}
                extensionToCall={voiceMessage.get('contact')}
                unknownContactNumber={voiceMessage.get('number')}
            />

            {voiceMessagesAction ? (
                <MenuItem
                    onClick={() =>
                        dispatch(
                            deleteVoiceMessageAction(
                                entityKey,
                                voiceMessage.get('uniqueId'),
                                getCallActionMarkedAsHandled(
                                    voiceMessagesAction,
                                ).get('key'),
                            ),
                        )
                    }
                >
                    {i18next.t('Queue.History.Unmark')}
                </MenuItem>
            ) : (
                <MenuItem
                    onClick={() =>
                        dispatch(
                            createVoiceMessageAction(
                                entityKey,
                                voiceMessage.get('uniqueId'),
                            ),
                        )
                    }
                >
                    {i18next.t('Queue.History.MarkAsHandled')}
                </MenuItem>
            )}

            <Divider className="my-2" />

            <MenuItem>
                <a
                    className="flex gap-2"
                    href={sharedVoiceMessageUrlPath(
                        entityKey,
                        voiceMessage.get('uniqueId'),
                    )}
                    download={true}
                >
                    <Icon iconKey="download" />
                    {i18next.t('call.voicemail.download')}
                </a>
            </MenuItem>

            <MenuItem
                className="gap-2"
                onClick={() =>
                    dispatch(
                        removeSharedVoiceMessage(
                            entityKey,
                            voiceMessage.get('uniqueId'),
                        ),
                    )
                }
            >
                <Icon iconKey="delete" />
                {i18next.t('call.voicemail.delete')}
            </MenuItem>
        </ContextMenu>
    );
};

export default PbxVoiceMessageContextMenu;
